// Säkerhetskopiering: paketerar projektets dokument, skyddsronder och rådata
// och lägger kopian på en extern lagringsplats (SharePoint/OneDrive).
// Syftet är att inget underlag ska saknas om systemet inte är tillgängligt.
import {
  collectExportGroups,
  downloadAllDocuments,
  type ExportDocGroup,
} from "@/lib/project/fullDocumentExport";
import {
  listDestinations,
  listSyncLog,
  pushFileToDestination,
  type DocumentDestination,
  type SyncLogRow,
} from "@/lib/integrations/destinations";

export const BACKUP_SUBPATH = "Sakerhetskopior";
export const BACKUP_DOCUMENT_TYPE = "backup";

export interface BackupProgress {
  current: number;
  total: number;
  label: string;
}

export interface BackupResult {
  ok: boolean;
  filename: string;
  fileCount: number;
  /** Dokument som saknar underlag och därför inte kunde säkerhetskopieras. */
  missing: string[];
  failed: string[];
  error?: string;
  /** Lokal kopia när ingen extern lagringsplats användes. */
  blob?: Blob;
}

/** Aktiva destinationer som kan ta emot en säkerhetskopia. */
export const listBackupDestinations = async (
  projectId: string
): Promise<DocumentDestination[]> =>
  (await listDestinations(projectId)).filter((d) => d.is_active);

/** Tidigare säkerhetskopior, hämtade ur synkloggen. */
export const listBackupHistory = async (projectId: string): Promise<SyncLogRow[]> =>
  (await listSyncLog(projectId)).filter((row) => row.document_type === BACKUP_DOCUMENT_TYPE);

const safetyRoundGroup = (group: ExportDocGroup) => group.docNumber.startsWith("27");

/**
 * Kör en säkerhetskopiering. Utan `destinationId` skapas kopian ändå och
 * returneras som blob så att den kan sparas manuellt.
 */
export const runProjectBackup = async (input: {
  projectId: string;
  destinationId?: string | null;
  includeRawData?: boolean;
  onProgress?: (progress: BackupProgress) => void;
}): Promise<BackupResult> => {
  const { projectId, destinationId, includeRawData = true, onProgress } = input;

  onProgress?.({ current: 0, total: 1, label: "Läser projektets dokument…" });
  const { groups, folders, projectName } = await collectExportGroups(projectId);

  // Allt med underlag följer med – dokument såväl som skyddsronder.
  const selectedKeys = groups.filter((g) => g.count > 0).map((g) => g.key);

  const result = await downloadAllDocuments(projectId, {
    groups,
    folders,
    selectedKeys,
    projectName,
    includeRawData,
    deliver: "return",
    onProgress: (p) => onProgress?.({ ...p, label: `Paketerar ${p.label}` }),
  });

  const filename = result.filename ?? `Sakerhetskopia_${projectId}.zip`;
  const missing = groups
    .filter((g) => g.count === 0)
    .map((g) => `Dok ${g.docNumber} ${g.title}${safetyRoundGroup(g) ? " (skyddsrond)" : ""}`);

  if (!destinationId) {
    return {
      ok: true,
      filename,
      fileCount: result.fileCount,
      missing,
      failed: result.failed,
      blob: result.blob,
    };
  }

  onProgress?.({ current: 1, total: 1, label: "Laddar upp till lagringsplatsen…" });
  const push = await pushFileToDestination({
    destinationId,
    projectId,
    filename,
    blob: result.blob!,
    subPath: BACKUP_SUBPATH,
    documentType: BACKUP_DOCUMENT_TYPE,
  });

  return {
    ok: push.ok,
    filename,
    fileCount: result.fileCount,
    missing,
    failed: result.failed,
    error: push.error,
    blob: push.ok ? undefined : result.blob,
  };
};
